import { useMemo, useCallback } from 'react';

import { useOktaAuth } from '@okta/okta-react';
import {
  useVerifyAuth as useVerifyAuthSDK,
  TAuth,
  IOktaAccessToken,
  IOktaIdToken,
} from '@sureapp/platform-sdk';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import {
  refreshOktaTokens,
  saveOktaIdToken,
  saveOktaAccessToken,
} from '@@/store/actions';
import { getOktaAccessToken, getOktaIdToken } from '@@/store/selectors';

import { isAbsolute } from './url';

function useVerifyAuth(redirectUrl?: string) {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const cachedHistory = useMemo(() => history, [history]);
  const oktaAccessToken = useSelector(getOktaAccessToken);
  const oktaIdToken = useSelector(getOktaIdToken);

  const { authService, authState } = useOktaAuth();

  const handleRefreshOktaTokens = useCallback(
    (auth: TAuth) => refreshOktaTokens(auth)(dispatch),
    [dispatch]
  );

  function handleSaveOktaIdToken(tokenObj: IOktaIdToken, auth: TAuth) {
    return saveOktaIdToken(tokenObj, auth)(dispatch);
  }

  function handleSaveOktaAccessToken(tokenObj: IOktaAccessToken, auth: TAuth) {
    return saveOktaAccessToken(tokenObj, auth)(dispatch);
  }

  function handleRedirect() {
    const to = redirectUrl || `${location.pathname}${location.search}`;

    if (isAbsolute(to)) {
      window.location.href = to;
    } else {
      cachedHistory.replace(`/login?redirectUrl=${to}`);
    }
  }

  return useVerifyAuthSDK({
    authService,
    authState,
    oktaAccessToken,
    oktaIdToken,
    refreshOktaTokens: handleRefreshOktaTokens,
    saveOktaIdToken: handleSaveOktaIdToken,
    saveOktaAccessToken: handleSaveOktaAccessToken,
    handleRedirect,
  });
}

export default useVerifyAuth;
